import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/article';
import { Helmet } from '@/pages/home/components/Helmet';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [id]);

  const article = id ? articles[id] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-background-100 mx-auto flex items-center justify-center mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您查找的文章不存在或已被删除</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full text-sm font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    articleSection: article.category,
    publisher: {
      '@type': 'Organization',
      name: '米乐体育',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.milesports.com/logo.png',
      },
    },
  };

  return (
    <>
      <Helmet
        title={`${article.title} - 米乐体育`}
        description={article.content.replace(/<[^>]*>/g, '').slice(0, 150)}
        keywords={article.tags.join(',')}
        canonical={`https://www.milesports.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
      />
      <script
        id="article-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/news" className="hover:text-primary-500 transition-colors cursor-pointer">新闻资讯</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>

        {/* Article Header */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <span className="text-xs font-bold text-accent-500 bg-accent-50 px-3 py-1 rounded-full">
              {article.category}
            </span>
          </div>
          <h1 className="font-bold text-2xl md:text-3xl text-foreground-900 leading-snug mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-xs"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-xs"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-xs"></i>
              {(article.views / 10000).toFixed(1)}万阅读
            </span>
          </div>
        </div>

        {/* Featured Image */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 mb-8">
          <div className="aspect-[16/9] rounded-xl overflow-hidden">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-[900px] mx-auto px-4 md:px-6">
          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>

        {/* Tags */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 mt-10 pt-6 border-t border-background-200/70">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">标签：</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category/news`}
                className="text-xs text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>

        {/* Related Articles */}
        <div className="max-w-[900px] mx-auto px-4 md:px-6 mt-12 mb-16">
          <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.values(articles)
              .filter((a) => a.id !== article.id)
              .slice(0, 3)
              .map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <span className="text-[10px] font-bold text-accent-500 bg-accent-50 px-2 py-0.5 rounded-full">
                      {related.category}
                    </span>
                    <h4 className="font-bold text-foreground-900 text-xs mt-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-[11px] text-foreground-400 mt-2">{related.date}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}